import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ChevronDown, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ensureDefaultBrand } from '../lib/ensureDefaultBrand';
import { usePermissions } from '../context/PermissionContext';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';
import { GENERATE_AUTH_TOKEN } from '../config';

interface CompanyRegistrationData {
  company_name: string;
  industry: string;
  address?: string; // Made optional since field is temporarily commented out
  contact_phone?: string; // Made optional since field is temporarily commented out
  logo_url?: string;
}

interface ServerResponse {
  response: 'success' | 'error';
  description: string;
}

export const OwnerOnboarding = () => {
  const navigate = useNavigate();
  const { refreshPermissions } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationData, setRegistrationData] = useState<CompanyRegistrationData>({
    company_name: '',
    industry: 'Moda e Indumentaria', // Set default industry to 'Moda e Indumentaria'
    // address field is now optional and omitted from initial state
    // contact_phone field is now optional and omitted from initial state
  });

  // Rubro select with custom option ("Otro") support
  const rubroOptions = [
    'Moda e Indumentaria',
    'Hogar y Decoración',
    'Belleza y Cuidado Personal',
    'Tecnología',
    'Salud y Bienestar',
    'Automotriz',
    'Gastronomía',
    'Juguetería',
    'Arte y Manualidades',
    'Librería',
    'Creación de Contenido',
    'Otro',
  ];
  const [industryOption, setIndustryOption] = useState<string>('Moda e Indumentaria');
  const [customIndustry, setCustomIndustry] = useState<string>('');

  useEffect(() => {
    const val = registrationData.industry;
    if (rubroOptions.includes(val)) {
      setIndustryOption(val);
      setCustomIndustry('');
    } else if (val && val.trim().length > 0) {
      setIndustryOption('Otro');
      setCustomIndustry(val);
    }
  }, [registrationData.industry]);

  // Get access code from location state if available

  const validateField = (value: string): boolean => {
    // Check for special characters except @ and . for email
    const specialCharsRegex = /[!#$%^&*()_+\-=\[\]{};':"\\|,<>/?]/;
    return !specialCharsRegex.test(value);
  };

  const validateForm = (): string | null => {
    // Check for empty fields
    for (const [key, value] of Object.entries(registrationData)) {
      // Logo, address, contact_phone are optional
      if (key === 'logo_url' || key === 'address' || key === 'contact_phone') continue; 
      if (!value && typeof value === 'string') {
        return `El campo ${key.replace('_', ' ')} es obligatorio`;
      }
    }

    /* Phone validation temporarily commented out
    // Validate phone format (allow numbers, spaces, and + symbol)
    const phoneRegex = /^[0-9\s+]+$/;
    if (registrationData.contact_phone && !phoneRegex.test(registrationData.contact_phone)) {
      return 'El teléfono solo puede contener números, espacios y el símbolo +';
    }
    */

    // Check for special characters in other fields (narrow to required string keys)
    const fieldsToValidate: Array<keyof Pick<CompanyRegistrationData, 'company_name' | 'industry'>> = ['company_name', 'industry'];
    for (const field of fieldsToValidate) {
      if (!validateField(registrationData[field])) {
        return `El campo ${String(field).replace('_', ' ')} no puede contener caracteres especiales`;
      }
    }

    return null;
  };

  const handleRegisterCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const dataWithUserId = {
        ...registrationData,
        // contact_phone field is temporarily commented out
        user_id: user.id
      };

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/analizar_nombre_y_logo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify(dataWithUserId),
      });

      const data: ServerResponse = await response.json();

      if (data.response === 'error') {
        throw new Error(data.description);
      }

      if (data.response === 'success') {
        // Refresh permissions before navigating to ensure they're updated
        await refreshPermissions();

        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company')
            .eq('id', user.id)
            .single();
          const companyId = (profile as any)?.id_company as string | null;
          if (companyId) {
            await supabase
              .from('company_profiles')
              .update({ rubro: registrationData.industry })
              .eq('id_company', companyId);
            mergeLastCrearAnuncioState({ rubro: registrationData.industry });
            // Crear la marca default en `brand_profiles` (idempotente,
            // best-effort). Antes la marca quedaba solo en company_profiles.
            await ensureDefaultBrand(companyId);
          }
        } catch {}

        navigate('/add-first-product', { replace: true });
      }
    } catch (err) {
      console.error('Error registering company:', err);
      setError(err instanceof Error ? err.message : 'Error al registrar la empresa');
      setLoading(false);
    }
  };

  /* ── Custom Rubro Select (no native OS dropdown) ── */
  const RubroSelect = () => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] bg-surface-card hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
        >
          <span className="text-brand-black/90">{industryOption}</span>
          <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 max-h-60 overflow-y-auto">
            {rubroOptions.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => {
                  setIndustryOption(r);
                  if (r !== 'Otro') {
                    setCustomIndustry('');
                    setRegistrationData({ ...registrationData, industry: r });
                  } else {
                    setRegistrationData({ ...registrationData, industry: customIndustry });
                  }
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-[15px] transition-colors flex items-center justify-between ${
                  industryOption === r ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <span>{r}</span>
                {industryOption === r && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white md:bg-surface-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md md:max-w-xl">

        {/* Registration Form */}
        <div className="bg-white md:bg-surface-card rounded-none md:rounded-lg shadow-none md:shadow-sm px-6 md:p-8">
            <h2 className="text-[30px] md:text-[22px] font-semibold text-brand-black/90 mb-8 leading-tight">
              ¿Y cómo se llama tu<br className="md:hidden" /> <span className="hidden md:inline"></span>marca?
            </h2>

          {error && (
            <div className="mb-5 p-3 bg-error-soft border border-error/20 rounded-md text-[13px] text-error-text flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleRegisterCompany} className="space-y-6">
              <div>
                <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
                  Nombre de la marca
                </label>
                <input
                  type="text"
                  value={registrationData.company_name}
                  onChange={(e) => setRegistrationData({...registrationData, company_name: e.target.value})}
                  className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
                  required
                />
              </div>

              <div>
                <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
                  Rubro
                </label>
                <div className="flex flex-col md:flex-row gap-3">
                  <div className={industryOption === 'Otro' ? 'md:w-1/2' : 'w-full'}>
                    <RubroSelect />
                  </div>
                  {industryOption === 'Otro' && (
                    <input
                      type="text"
                      value={customIndustry}
                      onChange={(e) => {
                        setCustomIndustry(e.target.value);
                        setRegistrationData({ ...registrationData, industry: e.target.value });
                      }}
                      placeholder="Especificá tu rubro"
                      className="w-full md:w-1/2 px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
                      required
                    />
                  )}
                </div>
              </div>

            {loading && (
              <div className="p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-md text-[13px] text-brand-primary flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <p>Creando perfil de empresa. Este proceso puede tomar unos segundos</p>
              </div>
            )}

            <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto h-[var(--btn-h)] px-8 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[15px] shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cargando...
                  </>
                ) : (
                  'Continuar'
                )}
              </button>
              <div />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};